import React, { useState } from 'react';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card } from '@/components/ui';
import { useMaintenanceContracts } from './hooks/useMaintenanceContracts';
import { ContractsDesktopTable } from './components/ContractsDesktopTable';
import { ContractsMobileList } from './components/ContractsMobileList';
import { ContractFormModal } from './components/ContractFormModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { MaintenanceContract } from './types/contracts.types';
import { useToast } from '@/components/ui';
import { contractStatusMap } from '@/utils/status';

interface ContractsPageProps {
  defaultType?: 'construction' | 'maintenance';
}

export default function ContractsPage({ defaultType }: ContractsPageProps) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const companyId = profile?.company_id || '';
  
  const [showCreate, setShowCreate] = useState(false);
  const [editContract, setEditContract] = useState<MaintenanceContract | null>(null);
  const [deleteContract, setDeleteContract] = useState<MaintenanceContract | null>(null);

  const {
    contracts,
    total,
    stats,
    isLoading,
    error,
    filters,
    setFilters,
    deleteContract: executeDelete,
    isDeleting
  } = useMaintenanceContracts(companyId, { type: defaultType });

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleDelete = async () => {
    if (!deleteContract) return;
    try {
      await executeDelete(deleteContract.id);
      setDeleteContract(null);
      toast.success('Đã xóa hợp đồng thành công');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  return (
    <PageLayout
      title={defaultType === 'construction' ? "Hợp đồng thi công" : "Hợp đồng bảo dưỡng"}
      description={defaultType === 'construction' 
        ? "Quản lý toàn bộ danh mục hợp đồng thi công, tiến độ thanh toán và khối lượng thực hiện."
        : "Quản lý toàn bộ danh mục hợp đồng bảo dưỡng, thời hạn và giá trị vận hành."
      }
      breadcrumbs={[
        { label: defaultType === 'construction' ? 'Thi công & Xây dựng' : 'Bảo trì & Bảo dưỡng' }, 
        { label: 'Hợp đồng' }
      ]}
      isLoading={isLoading}
      headerActions={
        <AuthButton 
          module="maintenance_contracts" 
          action="create" 
          variant="primary" 
          icon="ri-add-line" 
          onClick={() => setShowCreate(true)}
        >
          Tạo hợp đồng mới
        </AuthButton>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tổng hợp đồng"
            value={stats.total}
            icon="ri-file-list-3-line"
            variant="info"
          />
          <KPICard
            title="Đang hiệu lực"
            value={stats.active}
            icon="ri-checkbox-circle-line"
            variant="success"
          />
          <KPICard
            title="Sắp hết hạn"
            value={stats.nearExpiry}
            icon="ri-timer-2-line"
            variant="warning"
          />
          <KPICard
            title="Giá trị hợp đồng"
            value={stats.totalValue}
            format="compact-currency"
            suffix="VNĐ"
            icon="ri-money-dollar-circle-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-wrap gap-4 w-full justify-between items-center">
            <div className="flex gap-3">
              <Search
                placeholder="Tìm tên hợp đồng, mã hợp đồng..."
                value={filters.search}
                onSearch={handleSearchChange}
                className="w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(contractStatusMap).map(([val, cfg]) => ({ value: val, label: cfg.label }))
                    ]
                  }
                ]}
                onFilterChange={handleFilterChange}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" icon="ri-download-line">Xuất danh sách</Button>
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm hidden md:block">
          <ContractsDesktopTable 
            contracts={contracts} 
            type={defaultType}
            isLoading={isLoading}
            onEdit={setEditContract} 
            onDelete={setDeleteContract} 
          />
        </Card>

        <div className="md:hidden -mx-4">
          <ContractsMobileList 
            contracts={contracts}
            type={defaultType}
            onEdit={setEditContract}
            onDelete={setDeleteContract}
          />
        </div>
      </div>

      <ContractFormModal
        isOpen={showCreate || !!editContract}
        contract={editContract}
        companyId={companyId}
        defaultType={defaultType}
        onClose={() => {
          setShowCreate(false);
          setEditContract(null);
        }}
      />

      <ConfirmDialog
        isOpen={!!deleteContract}
        title="Xác nhận xóa hợp đồng"
        message={`Bạn có chắc chắn muốn xóa hợp đồng "${deleteContract?.contract_name}"? Hành động này sẽ chuyển trạng thái hợp đồng thành lưu trữ.`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteContract(null)}
      />
    </PageLayout>
  );
}
